import { useState } from "react";
import { t } from "ttag";

import {
  SettingsPageWrapper,
  SettingsSection,
} from "metabase/admin/components/SettingsSection";
import { useListPermissionsGroupsQuery, useListUsersQuery } from "metabase/api";
import { useSendCustomEmailMutation } from "metabase/api/email";
import {
  Alert,
  Button,
  Group,
  MultiSelect,
  Radio,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "metabase/ui";

type RecipientType = "all" | "users" | "groups";

export function CustomMailPage() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientType, setRecipientType] = useState<RecipientType>("all");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  // Cc/Bcc stay hidden until asked for, the way a mail client does it - most sends never
  // need them, and three always-visible recipient blocks buries the message fields.
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccUserIds, setCcUserIds] = useState<string[]>([]);
  const [ccGroupIds, setCcGroupIds] = useState<string[]>([]);
  const [bccUserIds, setBccUserIds] = useState<string[]>([]);
  const [bccGroupIds, setBccGroupIds] = useState<string[]>([]);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [sendCustomEmail, { isLoading }] = useSendCustomEmailMutation();

  // Fetched unconditionally now: Cc/Bcc can name users or groups whatever the To mode is.
  const { data: usersData } = useListUsersQuery({});
  const { data: groupsData } = useListPermissionsGroupsQuery(undefined);

  const userOptions =
    usersData?.data?.map((u) => ({
      value: String(u.id),
      label: `${u.common_name ?? `${u.first_name} ${u.last_name}`.trim()} (${u.email})`,
    })) ?? [];

  const groupOptions =
    groupsData?.map((g) => ({
      value: String(g.id),
      label: `${g.name} (${g.member_count} ${t`members`})`,
    })) ?? [];

  const handleSend = async () => {
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!subject.trim()) {
      setErrorMsg(t`Subject is required.`);
      return;
    }
    if (!body.trim()) {
      setErrorMsg(t`Body is required.`);
      return;
    }
    if (recipientType === "users" && selectedUserIds.length === 0) {
      setErrorMsg(t`Please select at least one user.`);
      return;
    }
    if (recipientType === "groups" && selectedGroupIds.length === 0) {
      setErrorMsg(t`Please select at least one group.`);
      return;
    }

    try {
      const result = await sendCustomEmail({
        subject,
        body,
        recipient_type: recipientType,
        user_ids:
          recipientType === "users" ? selectedUserIds.map(Number) : undefined,
        group_ids:
          recipientType === "groups" ? selectedGroupIds.map(Number) : undefined,
        cc_user_ids: ccUserIds.length ? ccUserIds.map(Number) : undefined,
        cc_group_ids: ccGroupIds.length ? ccGroupIds.map(Number) : undefined,
        bcc_user_ids: bccUserIds.length ? bccUserIds.map(Number) : undefined,
        bcc_group_ids: bccGroupIds.length ? bccGroupIds.map(Number) : undefined,
      }).unwrap();

      // Report the breakdown: the backend drops duplicates between the three fields, so
      // the totals can legitimately differ from what was selected.
      const parts = [t`${result.to} to`];
      if (result.cc > 0) {
        parts.push(t`${result.cc} cc`);
      }
      if (result.bcc > 0) {
        parts.push(t`${result.bcc} bcc`);
      }
      setSuccessMsg(t`Email sent to ${parts.join(", ")}.`);
      setSubject("");
      setBody("");
      setSelectedUserIds([]);
      setSelectedGroupIds([]);
      setCcUserIds([]);
      setCcGroupIds([]);
      setBccUserIds([]);
      setBccGroupIds([]);
    } catch (err: unknown) {
      const message =
        typeof err === "object" &&
        err !== null &&
        "data" in err &&
        typeof (err as { data: unknown }).data === "string"
          ? (err as { data: string }).data
          : t`Failed to send email. Please check your SMTP configuration.`;
      setErrorMsg(message);
    }
  };

  return (
    <SettingsPageWrapper
      title={t`Send Custom Email`}
      description={t`Compose and send a custom email to your users or groups.`}
    >
      <SettingsSection>
        <Stack gap="md">
          {successMsg && (
            <Alert
              color="success"
              title={t`Success`}
              withCloseButton
              onClose={() => setSuccessMsg(null)}
            >
              {successMsg}
            </Alert>
          )}
          {errorMsg && (
            <Alert
              color="error"
              title={t`Error`}
              withCloseButton
              onClose={() => setErrorMsg(null)}
            >
              {errorMsg}
            </Alert>
          )}

          <TextInput
            label={t`Subject`}
            placeholder={t`Enter email subject`}
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            required
          />

          <Textarea
            label={t`Body`}
            description={t`HTML is supported.`}
            placeholder={t`Enter email body…`}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            minRows={8}
            autosize
            required
          />

          <Stack gap="xs">
            <Text fw={500} size="sm">{t`Recipients`}</Text>
            <Radio.Group
              value={recipientType}
              onChange={(val) => {
                setRecipientType(val as RecipientType);
                setSelectedUserIds([]);
                setSelectedGroupIds([]);
              }}
            >
              <Stack gap="xs">
                <Radio value="all" label={t`All active users`} />
                <Radio value="users" label={t`Specific users`} />
                <Radio value="groups" label={t`Specific groups`} />
              </Stack>
            </Radio.Group>
          </Stack>

          {recipientType === "users" && (
            <MultiSelect
              label={t`Select users`}
              placeholder={t`Search users…`}
              data={userOptions}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              searchable
              clearable
            />
          )}

          {recipientType === "groups" && (
            <MultiSelect
              label={t`Select groups`}
              placeholder={t`Search groups…`}
              data={groupOptions}
              value={selectedGroupIds}
              onChange={setSelectedGroupIds}
              searchable
              clearable
            />
          )}

          {/* Cc / Bcc - revealed on demand, each addressable by users and/or groups */}
          <Group gap="sm">
            {!showCc && (
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => setShowCc(true)}
              >
                {t`Add Cc`}
              </Button>
            )}
            {!showBcc && (
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => setShowBcc(true)}
              >
                {t`Add Bcc`}
              </Button>
            )}
          </Group>

          {showCc && (
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text fw={500} size="sm">{t`Cc`}</Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => {
                    setShowCc(false);
                    setCcUserIds([]);
                    setCcGroupIds([]);
                  }}
                >
                  {t`Remove`}
                </Button>
              </Group>
              <MultiSelect
                placeholder={t`Cc users…`}
                data={userOptions}
                value={ccUserIds}
                onChange={setCcUserIds}
                searchable
                clearable
              />
              <MultiSelect
                placeholder={t`Cc groups…`}
                data={groupOptions}
                value={ccGroupIds}
                onChange={setCcGroupIds}
                searchable
                clearable
              />
            </Stack>
          )}

          {showBcc && (
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text fw={500} size="sm">{t`Bcc`}</Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => {
                    setShowBcc(false);
                    setBccUserIds([]);
                    setBccGroupIds([]);
                  }}
                >
                  {t`Remove`}
                </Button>
              </Group>
              <MultiSelect
                placeholder={t`Bcc users…`}
                data={userOptions}
                value={bccUserIds}
                onChange={setBccUserIds}
                searchable
                clearable
              />
              <MultiSelect
                placeholder={t`Bcc groups…`}
                data={groupOptions}
                value={bccGroupIds}
                onChange={setBccGroupIds}
                searchable
                clearable
              />
            </Stack>
          )}

          <Text c="text-secondary" size="xs">
            {t`Anyone already in To is left out of Cc, and anyone in To or Cc is left out of Bcc, so nobody gets the same email twice.`}
          </Text>

          <Button
            variant="filled"
            onClick={handleSend}
            loading={isLoading}
            w="fit-content"
          >
            {t`Send Email`}
          </Button>
        </Stack>
      </SettingsSection>
    </SettingsPageWrapper>
  );
}
